import { useAppDispatch, useAppSelector } from '@/store';
import { gameActions, gameSelector } from '@/store/reducers/game';
import { authSelector } from '@/store/reducers/auth';
import { levelTestActions } from '@/store/reducers/level-test';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import commonUtils from '@/utils/common';
import useFetchUnitsWithTopics from '@/hooks/UseUnits';
import UseScreenSize from '@/hooks/UseScreenSize';
import UnitsRoadmapSection from '@/components/UnitsRoadmapSection/UnitsRoadmapSection';
import { useSearchParams } from 'react-router-dom';
import UserUtils from '@/utils/user';
import GoToLevelTestDesktop from '@/components/GoToLevelTestDesktop/GoToLevelTestDesktop';
import WelcomeBanner from '@/components/WelcomeBanner';
import ProgressSection from '@/components/ProgressSection/ProgressSection';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GoToTutor from '@/components/GoToTutor/GoToTutor';

const MainViewDesktop = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = UseScreenSize();

  const { selectedType } = useAppSelector(gameSelector);
  const { skillSettings, user, levels } = useAppSelector(authSelector);
  const [selectedLevel, setSelectedLevel] = useState(() => {
    const savedLevel = localStorage.getItem('selectedLevel');
    return savedLevel ? parseInt(savedLevel, 10) : 1;
  });

  const dropdownOptions = levels
    .filter(item => item.id !== 10)
    .map(item => ({
      label: t(`levelName_${item.id}`),
      value: item.id,
    }));

  if (isMobile) {
    dropdownOptions.push({
      label: t('levelTest').toString(),
      value: -1,
    });
  }

  const setLevel = useCallback(
    (level: number) => {
      if (level === -1) {
        dispatch(levelTestActions.setActivity(null));
        navigate('/dashboard/level-test');
        return;
      }

      setSelectedLevel(level);
    },
    [navigate, dispatch]
  );

  const [searchParams] = useSearchParams();
  const isTeacher = UserUtils.isTeacher(user);
  const mapRef = useRef<null | HTMLDivElement>(null);
  const currentLevel = useMemo(() => {
    return isTeacher ? selectedLevel : user?.progress?.userLevel ?? 1;
  }, [isTeacher, selectedLevel, user?.progress?.userLevel]);

  const { units, isLoading } = useFetchUnitsWithTopics(currentLevel);

  useEffect(() => {
    if (!selectedType && skillSettings.length) {
      const firstSkill = skillSettings[0];
      const type = commonUtils.skillByName(firstSkill.name ?? '');

      dispatch(
        gameActions.setSelectedSkill({
          type: type,
          skill: firstSkill,
        })
      );
    }

    if (searchParams.get('skill')) {
      setTimeout(() => {
        executeScroll();
      }, 150);
    }
  }, []);

  const executeScroll = () => {
    if (mapRef.current !== null) {
      mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const mainViewComponents = (
    <>
      <UnitsRoadmapSection units={units} mapRef={mapRef} loading={isLoading} />
      {user?.metadata?.organizationDetails?.tutorData?.isTutorActive && !isMobile && <GoToTutor />}
      {isTeacher && !isMobile && <GoToLevelTestDesktop />}
    </>
  );

  const content = (
    <>
      <WelcomeBanner
        selectedLevel={selectedLevel}
        setSelectedLevel={setLevel}
        dropdownOptions={dropdownOptions}
      />
      {!isTeacher && <ProgressSection />}
      {mainViewComponents}
    </>
  );

  return content;
};

export default MainViewDesktop;
